import {
  SourcingCampaignIndex,
  SourcingChannelStatus,
} from 'api-clients/monolith';
import { defineMessages, MessageDescriptor } from 'react-intl';

import { POOL } from '@/types/channels';

export const campaignToggleTooltips = defineMessages({
  channel: {
    defaultMessage:
      'Pausing or resuming campaigns isn’t available yet for this channel.',
    description:
      'Tooltip shown when users attempt to pause or resume a campaign',
  },
  opening_not_public: {
    defaultMessage:
      'Make this opening active and Public to pause or resume its campaigns',
    description:
      'Tooltip on a disabled campaign toggle: the opening is inactive or not Public, and Public is the listing status that unlocks its campaigns',
  },
  opening_sourcing_off: {
    defaultMessage:
      'Activate this opening and enable sourcing to pause or resume its campaigns',
    description:
      'Tooltip on a disabled campaign toggle: the opening is inactive or has sourcing turned off',
  },
});

interface CampaignToggleStateParams {
  status: SourcingChannelStatus;
  isReadonly: boolean;
  /** Hire's `readonly_reason`: set only when the opening alone holds the campaign; absent from a Hire without the field. */
  readonlyReason?: SourcingCampaignIndex['readonly_reason'];
  channelSlug: string;
  isPublishable: boolean;
  isVonqChannel: boolean | null;
  isOtherChannel?: boolean;
}

interface CampaignToggleState {
  /** Whether to render the toggle at all (false once the campaign is done). */
  showToggle: boolean;
  /** Whether the toggle should be interactive. */
  isToggleEnabled: boolean;
  /** Why a readonly toggle is disabled, or null when no tooltip shows. */
  tooltip: MessageDescriptor | null;
}

/**
 * Shared pause/resume-toggle gating logic for campaign rows, used by both
 * the Channels page (`channels/campaigns/columns.tsx`) and the All
 * Campaigns page (`campaigns/campaigns-table/columns.tsx`) so the rules for
 * when a toggle is shown, enabled, or tooltipped stay in one place.
 */
export function getCampaignToggleState({
  status,
  isReadonly,
  readonlyReason,
  channelSlug,
  isPublishable,
  isVonqChannel,
  isOtherChannel = false,
}: CampaignToggleStateParams): CampaignToggleState {
  const isActionableStatus = status === 'active' || status === 'stopped';
  const isTogglableRow =
    !isOtherChannel && (isPublishable || !!isVonqChannel) && isActionableStatus;
  let tooltip: MessageDescriptor | null = null;
  if (isReadonly && channelSlug !== POOL && isActionableStatus) {
    // Name the opening only when fixing it would enable the toggle.
    tooltip =
      readonlyReason && isTogglableRow
        ? campaignToggleTooltips[readonlyReason]
        : campaignToggleTooltips.channel;
  }

  return {
    showToggle: status !== 'completed' && status !== 'failed',
    isToggleEnabled: !isReadonly && isTogglableRow,
    tooltip,
  };
}
